// src/pages/TestEmailCreation.js
import React, { useState } from 'react';
import { getFirestore, collection, addDoc, serverTimestamp, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

const TestEmailCreation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailList, setEmailList] = useState([]);
  const [formData, setFormData] = useState({
    subject: 'Test Email',
    from: 'test@example.com',
    textContent: 'This is a test email to debug the emails functionality.'
  });
  const { currentUser } = useAuth();
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const db = getFirestore();
      const userEmail = currentUser?.email || 'jane.doe@cooc.com.au';
      
      // Log info
      console.log(`Creating email in Users/${userEmail}/Emails`);
      console.log('Email data:', formData);
      
      // Create email document
      const emailData = {
        ...formData,
        to: userEmail,
        receivedAt: serverTimestamp(),
        isRead: false,
        createdAt: serverTimestamp()
      };
      
      // Add to Firestore
      const docRef = await addDoc(collection(db, 'Users', userEmail, 'Emails'), emailData);
      
      console.log(`Email created with ID: ${docRef.id}`);
      setSuccess(`Email created successfully with ID: ${docRef.id}`);
      
      // Clear form
      setFormData({
        subject: `Test Email ${new Date().toLocaleTimeString()}`,
        from: formData.from,
        textContent: formData.textContent
      });
      
      // Refresh email list
      fetchEmails();
    } catch (err) {
      console.error('Error creating email:', err);
      setError(`Failed to create email: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchEmails = async () => {
    try {
      const db = getFirestore();
      const userEmail = currentUser?.email || 'jane.doe@cooc.com.au';
      
      console.log(`Fetching emails from: Users/${userEmail}/Emails`);
      
      // Create query
      const emailsQuery = query(
        collection(db, 'Users', userEmail, 'Emails'),
        orderBy('receivedAt', 'desc'),
        limit(10)
      );
      
      // Execute query
      const querySnapshot = await getDocs(emailsQuery);
      
      console.log(`Query returned ${querySnapshot.size} emails`);
      
      // Process results
      const emails = [];
      querySnapshot.forEach((doc) => {
        emails.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setEmailList(emails);
    } catch (err) {
      console.error('Error fetching emails:', err);
      setError(`Error fetching emails: ${err.message}`);
    }
  };
  
  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    
    try {
      // Handle Firestore timestamps
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      console.error('Date formatting error:', err);
      return 'Invalid date';
    }
  };
  
  // Load emails on component mount
  React.useEffect(() => {
    fetchEmails();
  }, []);
  
  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '20px' }}>Test Email Creation</h1>
      <p style={{ marginBottom: '20px' }}>Use this utility to create test emails in your Firestore database.</p>
      
      <div style={{ 
        backgroundColor: '#f5f5f5',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label 
              htmlFor="subject" 
              style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}
            >
              Subject
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ddd'
              }}
              required
            />
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label 
              htmlFor="from" 
              style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}
            >
              From
            </label>
            <input
              type="email"
              id="from"
              name="from"
              value={formData.from}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ddd'
              }}
              required
            />
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label 
              htmlFor="textContent" 
              style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}
            >
              Content
            </label>
            <textarea
              id="textContent"
              name="textContent"
              value={formData.textContent}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                minHeight: '100px'
              }}
              required
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            style={{
              backgroundColor: '#128C7E',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '16px'
            }}
          >
            {loading ? 'Creating...' : 'Create Test Email'}
          </button>
        </form>
      </div>
      
      {error && (
        <div style={{ 
          backgroundColor: '#ffebee', 
          color: '#c62828', 
          padding: '15px', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}
      
      {success && (
        <div style={{ 
          backgroundColor: '#e8f5e9', 
          color: '#2e7d32', 
          padding: '15px', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <strong>Success:</strong> {success}
        </div>
      )}
      
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 style={{ margin: '0', fontSize: '18px' }}>Recent Emails</h2>
          <button
            onClick={fetchEmails}
            style={{
              backgroundColor: '#2196f3',
              color: 'white',
              border: 'none',
              padding: '8px 15px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Refresh List
          </button>
        </div>
        
        {emailList.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '20px',
            backgroundColor: '#f9f9f9',
            borderRadius: '4px' 
          }}>
            <p>No emails found in database.</p>
          </div>
        ) : (
          <div>
            {emailList.map(email => (
              <div 
                key={email.id} 
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  padding: '10px',
                  marginBottom: '10px',
                  backgroundColor: 'white'
                }}
              >
                <p style={{ fontWeight: '500', margin: '0 0 5px' }}>{email.subject || '(No subject)'}</p>
                <p style={{ color: '#666', fontSize: '14px', margin: '0 0 5px' }}>
                  From: {email.from || 'Unknown'} • {formatDate(email.receivedAt)}
                </p>
                {email.textContent && (
                  <p style={{ 
                    fontSize: '14px', 
                    margin: '10px 0 0',
                    color: '#333',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {email.textContent}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div style={{
        backgroundColor: '#eceff1',
        padding: '15px',
        borderRadius: '4px'
      }}>
        <h3 style={{ marginTop: '0' }}>Debug Information</h3>
        <p><strong>User:</strong> {currentUser ? currentUser.email : 'Not logged in (using default jane.doe@cooc.com.au)'}</p>
        <p><strong>Collection Path:</strong> Users/{currentUser?.email || 'jane.doe@cooc.com.au'}/Emails</p>
        <p><strong>Emails Found:</strong> {emailList.length}</p>
        
        <div style={{ marginTop: '15px' }}>
          <button
            onClick={() => console.log('Email List:', emailList)}
            style={{
              backgroundColor: '#673ab7',
              color: 'white',
              border: 'none',
              padding: '8px 15px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Log Emails to Console
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestEmailCreation;